import type { IconName } from '@speaking-coach/ui'
import { matchPath } from 'react-router-dom'

interface NavigationItemBase {
  id: string
  label: string
  icon: IconName
}

export interface AdminNavigationItem extends NavigationItemBase {
  to: string
  activePatterns: string[]
}

export interface AdminNavigationGroup {
  id: string
  label: string
  items: AdminNavigationItem[]
}

export type AdminFooterAction = NavigationItemBase

export const CALL_HISTORY_NAVIGATION: AdminNavigationItem = {
  id: 'sessions', label: 'Call history', icon: 'call', to: '/admin/sessions',
  activePatterns: ['/admin/sessions', '/admin/sessions/:sessionId'],
}

/**
 * One route inventory for the Admin shell.
 *
 * Routes remain declared in App.tsx: the inventory owns navigation and active
 * state, not lazy loading or route behavior. Feature branches add one item
 * here when their real route lands rather than editing the shell structure.
 */
export const ADMIN_NAVIGATION_GROUPS: AdminNavigationGroup[] = [
  {
    id: 'people-access',
    label: 'People & access',
    items: [
      {
        id: 'clients',
        label: 'Clients',
        icon: 'client',
        to: '/admin/clients',
        activePatterns: [
          '/admin/clients',
          '/admin/clients/new',
          '/admin/clients/:clientId',
          '/admin/clients/:clientId/history',
        ],
      },
      {
        id: 'cohorts',
        label: 'Cohorts',
        icon: 'cohort',
        to: '/admin/cohorts',
        activePatterns: [
          '/admin/cohorts',
          '/admin/clients/:clientId/cohorts/*',
        ],
      },
      {
        id: 'students',
        label: 'Students',
        icon: 'student',
        to: '/admin/students',
        activePatterns: ['/admin/students/*'],
      },
      {
        id: 'hr-users',
        label: 'HR users',
        icon: 'hrUser',
        to: '/admin/hr-users',
        activePatterns: [
          '/admin/hr-users',
          '/admin/clients/:clientId/hr-users/*',
        ],
      },
      {
        id: 'cooori-admins',
        label: 'Cooori admins',
        icon: 'admin',
        to: '/admin/admins',
        activePatterns: ['/admin/admins', '/admin/admins/*'],
      },
    ],
  },
  {
    id: 'content-quality',
    label: 'Content & quality',
    items: [
      {
        id: 'content',
        label: 'Programs',
        icon: 'program',
        to: '/admin/content',
        activePatterns: ['/admin/content', '/admin/content/programs/*'],
      },
      { id: 'content-levels', label: 'Levels', icon: 'level', to: '/admin/content/levels', activePatterns: ['/admin/content/levels', '/admin/content/levels/*'] },
      { id: 'content-themes', label: 'Themes', icon: 'theme', to: '/admin/content/themes', activePatterns: ['/admin/content/themes', '/admin/content/themes/*'] },
      { id: 'content-scenarios', label: 'Scenarios', icon: 'lesson', to: '/admin/content/scenarios', activePatterns: ['/admin/content/scenarios', '/admin/content/scenarios/*', '/admin/content/diagnostics/*'] },
      { id: 'content-tutors', label: 'Tutors', icon: 'coach', to: '/admin/content/tutors', activePatterns: ['/admin/content/tutors', '/admin/content/tutors/*'] },
      {
        id: 'content-vocabulary',
        label: 'Vocabulary',
        icon: 'vocabulary',
        to: '/admin/content/vocabulary',
        activePatterns: ['/admin/content/vocabulary'],
      },
      {
        id: 'content-coverage',
        label: 'Coverage',
        icon: 'coverage',
        to: '/admin/content/coverage',
        activePatterns: ['/admin/content/coverage'],
      },
      {
        id: 'content-authoring-guidelines',
        label: 'Authoring guidelines',
        icon: 'notes',
        to: '/admin/content/authoring-guidelines',
        activePatterns: ['/admin/content/authoring-guidelines'],
      },
      {
        id: 'content-history',
        label: 'Content history',
        icon: 'history',
        to: '/admin/content/history',
        activePatterns: ['/admin/content/history'],
      },
      {
        id: 'translations',
        label: 'Translations',
        icon: 'language',
        to: '/admin/translations',
        activePatterns: ['/admin/translations'],
      },
    ],
  },
  {
    id: 'email-delivery',
    label: 'Email delivery',
    items: [
      {
        id: 'email-test-send',
        label: 'Test email',
        icon: 'send',
        to: '/admin/email-test-send',
        activePatterns: ['/admin/email-test-send'],
      },
      {
        id: 'email-suppressions',
        label: 'Undeliverable addresses',
        icon: 'inviteFailed',
        to: '/admin/email-suppressions',
        activePatterns: ['/admin/email-suppressions/*'],
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { id: 'analytics', label: 'Analytics', icon: 'analytics', to: '/admin/analytics', activePatterns: ['/admin/analytics', '/admin/analytics/*'] },
      {
        id: 'activity',
        label: 'Activity',
        icon: 'auditLog',
        to: '/admin/activity',
        activePatterns: ['/admin/activity'],
      },
      {
        id: 'job-runs',
        label: 'Job runs',
        icon: 'jobRun',
        to: '/admin/jobs',
        activePatterns: ['/admin/jobs'],
      },
      CALL_HISTORY_NAVIGATION,
      { id: 'devices', label: 'Devices & browsers', icon: 'devices', to: '/admin/devices', activePatterns: ['/admin/devices'] },
      { id: 'feedback', label: 'Feedback', icon: 'feedback', to: '/admin/feedback', activePatterns: ['/admin/feedback', '/admin/feedback/*'] },
    ],
  },
]

/**
 * Operator help is deliberately outside Content & quality. It is code-owned
 * help for the people using this SPA, not the editorial Student FAQ product.
 */
export const ADMIN_FOOTER_ITEMS: AdminFooterAction[] = [
  {
    id: 'operator-faq',
    label: 'Operator FAQ',
    icon: 'help',
  },
]

export function isNavigationItemActive(
  pathname: string,
  item: AdminNavigationItem,
): boolean {
  return item.activePatterns.some((pattern) =>
    Boolean(matchPath({ path: pattern, end: true }, pathname)),
  )
}

/** The sidebar section owning this route, for shared page chrome. */
export function activeNavigationItem(
  pathname: string,
): AdminNavigationItem | undefined {
  return ADMIN_NAVIGATION_GROUPS.flatMap((group) => group.items).find(
    (item) => isNavigationItemActive(pathname, item),
  )
}
